import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { usePlatform } from '../../context/PlatformContext';
interface ScrollableRowProps {
  children: React.ReactNode;
  className?: string;
  gap?: string;
}
export const ScrollableRow: React.FC<ScrollableRowProps> = ({
  children,
  className = '',
  gap = 'gap-4'
}) => {
  const dk = useDarkClasses();
  const {
    isAfterTide
  } = usePlatform();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    if (scrollRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      // Use a small buffer (2px) to account for rounding errors
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 2);
    }
  };
  useEffect(() => {
    checkScroll();
    // Initial check might need a slight delay if images are loading
    const timeoutId = setTimeout(checkScroll, 100);
    window.addEventListener('resize', checkScroll);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScroll);
    };
  }, [children]);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.8;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  // Styling for arrows based on platform and dark mode
  const arrowBgClass = isAfterTide ? 'bg-[#1a1a2e]/90 text-[#f5f0e8] border-[#16213e]' : dk.isDark ? 'bg-gray-800/90 text-gray-200 border-gray-700' : 'bg-white/90 text-gray-700 border-gray-200';
  return <div className={`relative group/scroll ${className}`} data-id="element-3892" data-mp="2twh3jr8k0g2k">
      <div ref={scrollRef} onScroll={checkScroll} className={`flex overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory ${gap}`} style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-3893" data-mp="2tz3apprj8tk9">
        {children}
      </div>

      {/* Left Arrow */}
      {canScrollLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 rounded-full shadow-md border ${arrowBgClass} 
            hidden md:flex items-center justify-center p-2
            opacity-0 group-hover/scroll:opacity-100 transition-opacity duration-200`} aria-label="Scroll left" data-id="element-3894" data-mp="yv59e9j5ovhy">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-3895" data-mp="g32up3q2rpac" />
        </button>}

      {/* Right Arrow */}
      {canScrollRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 rounded-full shadow-md border ${arrowBgClass} 
            hidden md:flex items-center justify-center p-2
            opacity-0 group-hover/scroll:opacity-100 transition-opacity duration-200`} aria-label="Scroll right" data-id="element-3896" data-mp="3oi38csokivej">
          <ChevronRightIcon className="h-5 w-5" data-id="element-3897" data-mp="1q2dogz7ddb5k" />
        </button>}
    </div>;
};